import {
  EXTERIOR_ITEMS,
  FURNITURE_ITEMS,
  PLANT_ITEMS,
  RARE_ITEMS,
  type CatalogItem,
  type Footprint
} from "@tribe/domain";
import type { VoxelAsset } from "../../voxelTypes";
import { getExteriorCatalog } from "./ExteriorCatalog";
import { getFurnitureCatalog } from "./FurnitureCatalog";
import { getPetCatalog } from "./PetCatalog";
import { getPlantCatalog } from "./PlantCatalog";
import { getRareCatalog } from "./RareCatalog";
import { toExteriorFinish, toFabricColor, toPotStyle, toWoodFinish } from "./variantToVoxel";

/**
 * Finding the model for a catalogue item.
 *
 * The two catalogues were written independently and share no ids — `exterior-7` on one side,
 * `ext_creek_stones_new` on the other. What they do share is order: each bank of art was authored
 * against the same list, in the same sequence, so the nth catalogue item and the nth asset are the
 * same object. That correspondence is the bridge, and `bridge.test.ts` is what keeps it true.
 *
 * Matching on position rather than by parsing ids is deliberate — it leaves both id formats free
 * to change without silently re-pointing every model in the village.
 */

function ordinals(items: CatalogItem[]): Map<string, number> {
  return new Map(items.map((item, index) => [item.id, index]));
}

const EXTERIOR_ORDINALS = ordinals(EXTERIOR_ITEMS);
const FURNITURE_ORDINALS = ordinals(FURNITURE_ITEMS);
const PLANT_ORDINALS = ordinals(PLANT_ITEMS);
const RARE_ORDINALS = ordinals(RARE_ITEMS);

/** Generating a bank of art is not free, so each argument combination is built once. */
function memoise<Key extends string, Result>(build: (key: Key) => Result): (key: Key) => Result {
  const cache = new Map<Key, Result>();
  return (key) => {
    const cached = cache.get(key);
    if (cached) return cached;
    const built = build(key);
    cache.set(key, built);
    return built;
  };
}

const exteriorBank = memoise((finish: string) => getExteriorCatalog(toExteriorFinish(finish)));
const plantBank = memoise((pot: string) => getPlantCatalog(toPotStyle(pot)));
const furnitureBank = memoise((key: string) => {
  const [wood, fabric] = key.split("|");
  return getFurnitureCatalog(toWoodFinish(wood), toFabricColor(fabric));
});
const rareBank = memoise(() => getRareCatalog());
const petBank = memoise(() => getPetCatalog());

/**
 * The voxel model for an item as configured, or null when it has none.
 *
 * Homes and rooms return null on purpose: a home is built by `generateVoxelCottage` from the
 * property's chosen type, and a room is a space rather than a thing standing in one.
 */
export function resolveVoxelAsset(item: CatalogItem, variants: Record<string, string>): VoxelAsset | null {
  switch (item.category) {
    case "exterior": {
      const ordinal = EXTERIOR_ORDINALS.get(item.id);
      return ordinal === undefined ? null : exteriorBank(variants.finish ?? "new")[ordinal] ?? null;
    }
    case "plant": {
      const ordinal = PLANT_ORDINALS.get(item.id);
      return ordinal === undefined ? null : plantBank(variants.pot ?? "terracotta")[ordinal] ?? null;
    }
    case "furniture": {
      const ordinal = FURNITURE_ORDINALS.get(item.id);
      const key = `${variants.wood ?? "oak"}|${variants.fabric ?? "cream"}`;
      return ordinal === undefined ? null : furnitureBank(key)[ordinal] ?? null;
    }
    case "rare": {
      const ordinal = RARE_ORDINALS.get(item.id);
      return ordinal === undefined ? null : rareBank("")[ordinal] ?? null;
    }
    case "pet": {
      // Pets are the one category where both sides already speak the same language.
      const species = item.id.replace(/^pet-/, "");
      const appearance = variants.appearance ?? "";
      return petBank("").find((asset) => asset.id === `pet_${species}_${appearance}`) ?? null;
    }
    default:
      return null;
  }
}

/**
 * The ground an asset actually covers.
 *
 * Measured from the voxels rather than read off `VoxelAsset.dimensions`, which is a decorative
 * field and demonstrably wrong — the stone bird bath declares 7 x 7 and spans nearly 7.7. A
 * boundary rule built on the declared number would let items hang over the fence.
 */
export function measureFootprint(asset: VoxelAsset): Footprint {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let height = 0;
  // Never above the origin, matching how the renderer computes the offset it stands art on.
  let baseY = 0;

  for (const voxel of asset.voxels) {
    const halfX = (voxel.scaleX ?? voxel.scale ?? 1) / 2;
    const halfY = (voxel.scaleY ?? voxel.scale ?? 1) / 2;
    const halfZ = (voxel.scaleZ ?? voxel.scale ?? 1) / 2;
    minX = Math.min(minX, voxel.x - halfX);
    maxX = Math.max(maxX, voxel.x + halfX);
    minZ = Math.min(minZ, voxel.z - halfZ);
    maxZ = Math.max(maxZ, voxel.z + halfZ);
    height = Math.max(height, voxel.y + halfY);
    baseY = Math.min(baseY, voxel.y - halfY);
  }

  if (!asset.voxels.length) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0, height: 0, baseY: 0, surfaceY: 0 };
  return { minX, maxX, minZ, maxZ, height, baseY, surfaceY: measureSurface(asset, baseY) };
}

/**
 * The height of an item's broadest upper surface, measured above its base.
 *
 * Voxels are grouped by where their *top face* sits, and the surface is the highest such level that
 * is still wide — at least a third as many voxels as the widest level the item has. A table's top
 * is dozens of voxels and the bowl standing on it is three, so the table wins; a plain box has one
 * level and it wins by default.
 *
 * This is what stacking measures against, so it has to be a real surface and not the top of the
 * bounding box, which is usually some ornament the art put there.
 */
function measureSurface(asset: VoxelAsset, baseY: number): number {
  const levels = new Map<number, number>();
  for (const voxel of asset.voxels) {
    const top = voxel.y + (voxel.scaleY ?? voxel.scale ?? 1) / 2;
    // Rounded, so voxels meant to form one surface are not split across floating-point neighbours.
    const key = Math.round(top * 100) / 100;
    levels.set(key, (levels.get(key) ?? 0) + 1);
  }

  const widest = Math.max(...levels.values());
  let surface = baseY;
  for (const [top, count] of levels) {
    if (count >= widest / 3 && top > surface) surface = top;
  }
  return surface - baseY;
}

/** The largest silhouette an item has across everything it can be configured as. */
export function measureItemFootprint(item: CatalogItem): Footprint | null {
  let largest: Footprint | null = null;
  for (const variants of variantCombinations(item)) {
    const asset = resolveVoxelAsset(item, variants);
    if (!asset) continue;
    const measured = measureFootprint(asset);
    largest = largest
      ? {
          minX: Math.min(largest.minX, measured.minX),
          maxX: Math.max(largest.maxX, measured.maxX),
          minZ: Math.min(largest.minZ, measured.minZ),
          maxZ: Math.max(largest.maxZ, measured.maxZ),
          height: Math.max(largest.height, measured.height),
          baseY: Math.min(largest.baseY ?? 0, measured.baseY ?? 0),
          // The lowest surface across the variants, so a thing put on top of any of them rests on
          // the item rather than inside it.
          surfaceY: Math.min(largest.surfaceY ?? 0, measured.surfaceY ?? 0)
        }
      : measured;
  }
  return largest;
}

/** Every variant combination an item can be rendered in, for measuring its largest silhouette. */
export function variantCombinations(item: CatalogItem): Record<string, string>[] {
  const axes = Object.entries(item.variants);
  if (!axes.length) return [{}];
  return axes.reduce<Record<string, string>[]>(
    (combinations, [name, values]) =>
      combinations.flatMap((combination) => values.map((value) => ({ ...combination, [name]: value }))),
    [{}]
  );
}
